import { z } from 'zod'
import { util, $ZodError } from 'zod/v4/core'

const objectSchema = <T extends z.ZodRawShape>(ZodRawShape: T) => z.object(ZodRawShape)

const arraySchema = <T extends z.ZodType>(val: T) => z.array(val)

const unionSchema = <T extends [z.ZodType, z.ZodType, ...z.ZodType[]]>(types: T) => z.union(types)

const recordSchema = <T extends z.ZodType>(valueSchema: T) => z.record(stringSchema, valueSchema)

const literalSchema = <T extends util.Literal>(val: T) => z.literal(val)

const stringSchema = z.string()
const emailSchema = z.email()
const emailWithEmptySchema = unionSchema([emailSchema, literalSchema('')])

const idSchema = z.ulid()
const hashSchema = z.string().regex(/^[A-Za-z0-9+/=]+\.[a-f0-9]{64}$/)
const publicHashSchema = z.string().regex(/^[A-Za-z0-9+/=]{32,64}$/)

const numberSchema = z.number()

const booleanSchema = z.boolean()

const booleanFromStringSchema = stringSchema.transform(value =>
  ['true', 'yes', '1', 'on'].includes(value.toLowerCase())
)

const secretSchema = stringSchema.min(15)

const dateTimeSchema = z.iso.datetime()
const urlSchema = z.url()
const urlWithEmptySchema = unionSchema([urlSchema, literalSchema('')])

const enumSchema = <const T extends readonly string[]>(enumArr: T) => z.enum(enumArr, { error: 'Required field.' })

type FileOptions = {
  maxSize: number
  acceptedMimeTypes: string[]
  acceptedTypesDescription: string
  required?: boolean
  maxFiles?: number
}

const createFileSchema = (options: FileOptions) => {
  const { maxSize, acceptedMimeTypes, acceptedTypesDescription, required = false } = options
  // Base schema to handle single file input
  let schema: z.ZodType<File | undefined> = z
    .any()
    .transform(files => {
      if (files instanceof FileList && files.length > 0) {
        return files[0]
      }
      if (files instanceof File) {
        return files
      }
      return undefined // Return undefined instead of null
    })
    .refine(
      file => {
        if (!file) return !required // If not required and no file, it's valid
        return file instanceof File
      },
      { error: required ? 'File is required.' : 'Invalid file type.' }
    )
    .refine(
      file => {
        if (!file) return !required // Skip validation if not required and no file
        return file.size <= maxSize
      },
      {
        error: `Max file size is ${maxSize / 1024 / 1024} MB.`,
      }
    )
    .refine(
      file => {
        if (!file) return !required // Skip validation if not required and no file
        return acceptedMimeTypes.includes(file.type)
      },
      {
        error: acceptedTypesDescription,
      }
    )

  // Apply optional based on the 'required' flag
  if (!required) {
    schema = schema.optional() // Allow undefined
  }

  return schema
}

const createMultipleFilesSchema = (options: FileOptions) => {
  const { maxSize, acceptedMimeTypes, acceptedTypesDescription, required = false, maxFiles } = options
  // Base schema to handle multiple files input
  let schema: z.ZodType<File[] | undefined> = z
    .any()
    .optional()
    .transform(files => {
      if (files instanceof FileList) {
        return Array.from(files)
      }
      if (Array.isArray(files)) {
        return files
      }
      return undefined // Return undefined instead of empty array or null
    })
    .refine(
      files => {
        if (required) {
          return files !== undefined && files.length > 0
        }
        return true // If not required, any number of files (including 0) is valid
      },
      { error: required ? 'At least one file is required.' : undefined }
    )
    .refine(
      files => {
        if (files === undefined) return !required // If no files and not required, valid
        return files.every(file => file instanceof File)
      },
      { error: 'One or more files are invalid.' }
    )
    .refine(
      files => {
        if (files === undefined) return !required // Skip validation if not required and no files
        return files.every(file => file.size <= maxSize)
      },
      { error: `Each file must be smaller than ${maxSize / 1024 / 1024} MB.` }
    )
    .refine(
      files => {
        if (files === undefined) return !required // Skip validation if not required and no files
        return files.every(file => acceptedMimeTypes.includes(file.type))
      },
      { error: acceptedTypesDescription }
    )

  // Maximum number of files validation
  if (maxFiles !== undefined) {
    schema = schema.refine(
      files => {
        if (files === undefined) return !required // If not required and no files, valid
        return files.length <= maxFiles
      },
      { error: `You can upload up to ${maxFiles} files.` }
    )
  }

  // Apply optional based on the 'required' flag
  if (!required) {
    schema = schema.optional() // Allow undefined
  }

  return schema
}

const zCustomIssue = 'custom'

const isInstanceOfZodError = (error: unknown): error is $ZodError => error instanceof $ZodError

export {
  objectSchema,
  arraySchema,
  unionSchema,
  recordSchema,
  stringSchema,
  idSchema,
  hashSchema,
  publicHashSchema,
  numberSchema,
  emailSchema,
  emailWithEmptySchema,
  literalSchema,
  booleanSchema,
  booleanFromStringSchema,
  secretSchema,
  dateTimeSchema,
  urlSchema,
  urlWithEmptySchema,
  enumSchema,
  createFileSchema,
  createMultipleFilesSchema,
  zCustomIssue,
  isInstanceOfZodError,
}

export type ZInferType<T extends z.ZodType> = z.infer<T>
